import * as React from 'react'
import {
  useReviewTabularIngestPageMutation,
  type TabularEditCounts,
  type TabularIngestJobDetail
} from '@axilon-apps/domains-ontology-ui-state'
import { extractErrorMessage } from '../IngestPage/util/extractErrorMessage'
import { useRunPageEdits, type RunPageEdits } from './useRunPageEdits'

export type RunReviewDecisions = NonNullable<TabularIngestJobDetail['review']>['pages']
export type RunReviewDecision = RunReviewDecisions[string]['decision']

/** A run's page review as the pages workspace shows it, and what the reader may change. */
export interface RunPageReview {
  decisions: RunReviewDecisions
  /** The run is still held, so decisions can change. */
  open: boolean
  /** The run is held for its flagged pages, so any page that is not excluded takes row edits. */
  editable: boolean
  /** How many saved row edits each page has. */
  edited: Record<string, TabularEditCounts>
  canManage: boolean
  /** The page whose decision is being saved. */
  saving?: string
  error?: string
  /** Record a decision, or `null` to open the page again. Resolves false when it was refused. */
  decide: (pageId: string, decision: RunReviewDecision | null) => Promise<boolean>
  edits: RunPageEdits
}

const NO_EDITS: Record<string, TabularEditCounts> = {}

/** The review of a run that was held for it; none for a run that never was. */
export function useRunPageReview(
  detail: TabularIngestJobDetail | undefined,
  canManage: boolean
): RunPageReview | undefined {
  const [review] = useReviewTabularIngestPageMutation()
  const [saving, setSaving] = React.useState<string>()
  const [error, setError] = React.useState<string>()
  const jobId = detail?.id
  const edits = useRunPageEdits(jobId)
  React.useEffect(() => setError(undefined), [jobId])
  const decide = React.useCallback(
    async (pageId: string, decision: RunReviewDecision | null) => {
      if (!jobId) return false
      setSaving(pageId)
      setError(undefined)
      try {
        await review({ jobId, pageId, decision }).unwrap()
        return true
      } catch (caught) {
        setError(extractErrorMessage(caught, 'The decision could not be saved.'))
        return false
      } finally {
        setSaving(undefined)
      }
    },
    [jobId, review]
  )
  if (!detail?.review) return undefined
  const open = detail.state === 'awaiting_review' || detail.state === 'awaiting_turn'
  return {
    decisions: detail.review.pages,
    open,
    editable: open && (detail.pages ?? []).some((page) => page.status === 'needs_review'),
    edited: detail.review.edited ?? NO_EDITS,
    canManage,
    saving,
    error,
    decide,
    edits
  }
}
